/**
 * Logique pure des projets (lot D1a) : étapes cochées d'après les dates, décalage d'un projet, phase en
 * cours. Portage des fonctions `stepsOf` et du glisser-déposer (`pointerup`) de la maquette
 * (`docs/maquette/maquette-v7.html`, bloc « PROJETS »), avec les dates `AAAA-MM-JJ`.
 */
import { addDays } from './dates.js';
import { DEFAULT_INTEGRATION_WEEKS, PHASE_KEYS, type PhaseKey } from './planning.js';

export type Phases = Partial<Record<PhaseKey, string | null>>;

/** Libellés des phases (légende du Gantt, maquette `PH`). */
export const PHASE_LABELS: Record<PhaseKey, string> = {
  R3: 'R3',
  Maquette: 'Maquette',
  Ajustements: 'Ajustements',
  R4: 'R4 · présentation',
  Retours: 'Retours',
  Integration: 'Intégration',
  R5: 'R5 · mise en ligne',
};

/** Rendez-vous (losanges du Gantt, événements de l'agenda). */
export const MEETING_PHASE_KEYS = ['R3', 'R4', 'R5'] as const;
export type MeetingPhaseKey = (typeof MEETING_PHASE_KEYS)[number];

export function isPhaseKey(value: unknown): value is PhaseKey {
  return typeof value === 'string' && (PHASE_KEYS as readonly string[]).includes(value);
}

/**
 * Phase en cours au jour `today` (maquette : `cur`) : la dernière phase, dans l'ordre R3 → R5, dont la date
 * est passée ou égale à aujourd'hui ; null si le projet n'a pas commencé.
 */
export function currentPhase(phases: Phases, today: string): PhaseKey | null {
  let current: PhaseKey | null = null;
  for (const key of PHASE_KEYS) {
    const date = phases[key];
    if (date != null && date <= today) current = key;
  }
  return current;
}

/**
 * Étape du modèle cochée d'après les dates du projet (maquette : `stepsOf`) :
 * - une étape d'une phase antérieure à la phase en cours est faite ;
 * - une étape de la phase en cours est faite si la phase a commencé AVANT aujourd'hui, sauf l'intégration ;
 * - une étape « R4 » d'un projet sans R4 compte comme une étape « Retours » ;
 * - une étape « R3 » d'un projet sans R3 est faite si la maquette a commencé.
 */
export function stepDoneFromDates(stepPhase: PhaseKey, phases: Phases, today: string): boolean {
  const started = (key: PhaseKey): boolean => {
    const date = phases[key];
    return date != null && date < today;
  };
  const current = currentPhase(phases, today);
  const currentIndex = current ? PHASE_KEYS.indexOf(current) : -1;
  if (phases[stepPhase] == null && stepPhase === 'R3') return started('Maquette');
  const effective: PhaseKey = stepPhase === 'R4' && phases.R4 == null ? 'Retours' : stepPhase;
  const index = PHASE_KEYS.indexOf(effective);
  return (
    index < currentIndex || (index === currentIndex && started(stepPhase) && stepPhase !== 'Integration')
  );
}

/**
 * Décale un projet de `days` jours (maquette : glisser une phase, ou « Décaler le projet… ») : toutes les
 * phases à partir de `fromPhase` (incluse), ou toutes si `fromPhase` est absente, avancent de `days` jours.
 * Les phases sans date restent sans date.
 */
export function shiftPhases(phases: Phases, days: number, fromPhase?: PhaseKey): Phases {
  const fromIndex = fromPhase ? PHASE_KEYS.indexOf(fromPhase) : 0;
  const shifted: Phases = {};
  PHASE_KEYS.forEach((key, index) => {
    const date = phases[key];
    if (date === undefined) return;
    shifted[key] = date != null && index >= fromIndex ? addDays(date, days) : date;
  });
  return shifted;
}

/** Phases dont la date change entre deux versions (dans l'ordre R3 → R5). */
export function changedPhases(before: Phases, after: Phases): PhaseKey[] {
  return PHASE_KEYS.filter((key) => (before[key] ?? null) !== (after[key] ?? null));
}

const WEEKDAYS_SHORT = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'] as const;
const MONTHS_SHORT = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
] as const;
const WEEKDAYS_LONG = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'] as const;
const MONTHS_LONG = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
] as const;

function parts(date: string): { year: number; month: number; day: number; weekday: number } {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  const js = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return { year, month, day, weekday: js === 0 ? 7 : js };
}

/** « lun. 12 oct. » (maquette : `shortD`). */
export function formatWeekdayShort(date: string): string {
  const p = parts(date);
  return `${WEEKDAYS_SHORT[p.weekday - 1]} ${p.day} ${MONTHS_SHORT[p.month - 1]}`;
}

/** « lundi 18 janvier 2027 » ; l'année est omise si c'est celle de `refDate` (maquette : `longDay`). */
export function formatDayLong(date: string, refDate?: string): string {
  const p = parts(date);
  const sameYear = refDate !== undefined && parts(refDate).year === p.year;
  return `${WEEKDAYS_LONG[p.weekday - 1]} ${p.day} ${MONTHS_LONG[p.month - 1]}${sameYear ? '' : ` ${p.year}`}`;
}

/* ------------------------------------------------------------------ devis signés (lot D1b) */

/** Catégories de produits d'un devis (`quotes.lines[].category`), sans doublon, dans l'ordre. */
export function quoteCategories(lines: readonly { category?: string | null }[]): string[] {
  return [...new Set(lines.map((l) => l.category).filter((c): c is string => !!c))];
}

/**
 * Nature d'un devis pour le simulateur (maquette `SIGNED` : « site e-commerce », « site vitrine »,
 * « vitrine et boutique ») d'après les catégories de ses lignes ; null si rien de reconnu.
 */
export function quoteKindLabel(categories: readonly string[]): string | null {
  const has = (c: string) => categories.includes(c);
  if (has('ecommerce') && has('vitrine')) return 'vitrine et boutique';
  if (has('ecommerce')) return 'site e-commerce';
  if (has('refonte')) return 'refonte';
  if (has('vitrine')) return 'site vitrine';
  return null;
}

/**
 * Durée d'intégration proposée par défaut : celle des Réglages (`planning.defaultIntegrationWeeks`, 2 semaines
 * par défaut), une semaine de plus pour une boutique (maquette : 3 semaines pour une boutique, 2 sinon), au plus 3.
 */
export function defaultIntegrationWeeks(
  categories: readonly string[],
  base: number = DEFAULT_INTEGRATION_WEEKS,
): 1 | 2 | 3 {
  const weeks = Math.min(3, Math.max(1, Math.round(base))) as 1 | 2 | 3;
  return categories.includes('ecommerce') ? (Math.min(3, weeks + 1) as 1 | 2 | 3) : weeks;
}

/** Au-delà de 14 jours avant le R3, le démarrage est « différé » : les rendez-vous restent à confirmer. */
export const DEFERRED_START_DAYS = 14;
